<template>
  <v-card class="d-flex flex-column align-center justify-center pa-5">
    <v-card-title class="mb-5"
      >Νέος Διαχειριστής Αμοιβαίων Κεφαλαίων<br />
      <span class="body-2 font-weight-medium">
        ({{ currentCreditInstitute.mmfManagerNationalName }})
      </span>
    </v-card-title>
    <v-form class="form d-flex flex-column justify-center">
      <v-text-field
        label="Όνομα"
        v-model="name"
        class="rounded text-body-1 font-weight-regular"
        :error-messages="nameErrors"
      >
      </v-text-field>

      <v-text-field
        label="Επώνυμο"
        v-model="lastName"
        class="rounded text-body-1 font-weight-regular"
        :error-messages="lastNameErrors"
      >
      </v-text-field>

      <v-text-field
        label="Email"
        v-model="email"
        class="rounded text-body-1 font-weight-regular"
        :error-messages="emailErrors"
      >
      </v-text-field>

      <v-text-field
        label="Κωδικός"
        v-model="password"
        type="password"
        class="rounded text-body-1 font-weight-regular"
        :error-messages="passwordErrors"
      >
      </v-text-field>

      <v-text-field
        label="Επιβεβαίωση κωδικού"
        v-model="passwordConfirmation"
        type="password"
        class="rounded text-body-1 font-weight-regular"
        :error-messages="passwordConfirmationErrors"
      >
      </v-text-field>

      <v-radio-group label="Σύνδεση" v-model="canLogin">
        <v-radio label="Επιτρέπεται" :value="true"></v-radio>
        <v-radio label="Απογορεύεται" :value="false"></v-radio>
      </v-radio-group>

      <div class="d-flex justify-center">
        <v-btn
          class="success rounded text-sm-body-1 font-weight-regular mr-5"
          :loading="loader"
          @click="onSubmit"
        >
          <span>Προσθήκη</span>
        </v-btn>
        <v-btn
          class="secondary rounded text-sm-body-1 font-weight-regular"
          @click="$emit('closeDialog')"
        >
          <span>Επιστροφή</span>
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import axios from "axios";
import {
  required,
  email,
  minLength,
  maxLength,
  sameAs,
} from "vuelidate/lib/validators";

export default {
  props: {
    currentCreditInstitute: Object,
  },

  data() {
    return {
      name: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      canLogin: true,
      loader: false,
    };
  },

  validations: {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(16),
    },
    lastName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(16),
    },

    email: {
      required,
      email,
    },
    password: {
      required,
      minLength: minLength(8),
      containsSpecial: function (value) {
        return /[#?!@$%^&*-]/.test(value);
      },
      containsNumber: function (value) {
        return /[0-9]/.test(value);
      },
    },
    passwordConfirmation: {
      required,
      sameAsPassword: sameAs("password"),
    },
  },

  computed: {
    nameErrors() {
      const errors = [];
      if (!this.$v.name.$dirty) return errors;
      if (!this.$v.name.required) errors.push("Υποχρεωτικό πεδίο");
      if (!this.$v.name.minLength) errors.push("Τουλάχιστον 3 χαρακτήρες");
      if (!this.$v.name.maxLength) errors.push("Μέχρι 16 χαρακτήρες");
      return errors;
    },

    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      if (!this.$v.lastName.required) errors.push("Υποχρεωτικό πεδίο");
      if (!this.$v.lastName.minLength) errors.push("Τουλάχιστον 3 χαρακτήρες");
      if (!this.$v.lastName.maxLength) errors.push("Μέχρι 16 χαρακτήρες");
      return errors;
    },

    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      if (!this.$v.email.required) errors.push("Υποχρεωτικό πεδίο");
      if (!this.$v.email.email) errors.push("Το email δεν είναι έγκυρο");

      return errors;
    },

    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      if (!this.$v.password.required) errors.push("Υποχρεωτικό πεδίο");
      if (!this.$v.password.minLength) errors.push("Τουλάχιστον 8 χαρακτήρες");
      if (!this.$v.password.containsSpecial)
        errors.push(
          "Τουλάχιστον ένα σύμβολο (#?!@$%^&*-) ,και έναν αριθμό (0-9)"
        );
      if (!this.$v.password.containsNumber)
        errors.push(
          "Τουλάχιστον ένα σύμβολο (#?!@$%^&*-) ,και έναν αριθμό (0-9)"
        );

      return errors;
    },

    passwordConfirmationErrors() {
      const errors = [];
      if (!this.$v.passwordConfirmation.$dirty) return errors;
      if (!this.$v.passwordConfirmation.required)
        errors.push("Υποχρεωτικό πεδίο");
      if (!this.$v.passwordConfirmation.sameAsPassword)
        errors.push("Οι κωδικοί δεν ταιριάζουν. Δοκιμάστε ξανά");
      return errors;
    },
  },

  methods: {
    async onSubmit() {
      try {
        this.$v.$touch();
        if (this.$v.$invalid) return;
        this.loader = true;
        await axios.post(`${process.env.VUE_APP_BASE_URL}/admin/Register`, {
          firstName: this.name,
          lastName: this.lastName,
          userEmail: this.email,
          userPassword: this.password,
          canLogin: this.canLogin,
          userRole: "credit",
          creditInstituteIds: [this.currentCreditInstitute.creditInstituteId],
        });

        this.loader = false;

        await this.$emit("getUsers");

        this.$store.commit("toggleSnackbar", {
          open: true,
          color: "success",
          text: "Η προσθήκη  διαχειριστή ολοκληρώθηκε με επιτυχία!",
        });

        this.$emit("closeDialog");
      } catch {
        this.loader = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.form {
  width: 400px;
}
</style>